import React, { useRef, useState, useMemo, useCallback } from 'react';

import { Button, Grid, makeStyles, Typography } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import ReCAPTCHA from 'react-google-recaptcha';
import { GoogleReCaptcha, GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { useHistory } from 'react-router-dom';

import ValidationException from '../../exceptions/ValidationException';
import User from '../../models/User';
import AuthService from '../../services/auth.service';
import EditFields from '../common/EditFields';

const useStyles = makeStyles(() => {
  return {
    buttonContainer: {
      marginTop: '1rem',
    },
    email: {
      height: '130px',
    },
    errorMessage: {
      color: '#B12A2A',
      marginTop: '8px',
    },
    fieldContainer: {
      margin: '0px 0px 20px',
    },
    formTitle: {
      color: '#172B4D',
      fontSize: '30px',
      fontWeight: 'bold',
      marginBottom: '16px',
    },
  };
});

export default () => {
  const editFieldRef = useRef();
  const classes = useStyles();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [recaptchaV2Token, setRecaptchaV2Token] = useState(null);
  const [useV3Captcha, setUseV3Captcha] = useState(true);
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);
  const [errors, setErrors] = useState();

  const fields = useMemo(
    () => [
      {
        containerClass: classes.fieldContainer,
        label: 'Email Address',
        margin: 'normal',
        name: 'email',
        placeholder: 'Email Address',
        required: true,
        resetHelperText: true,
        type: 'text',
        variant: 'standard',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onSubmit = async (data) => {
    try {
      if (useV3Captcha) {
        await new AuthService().forgotPassword(data);
      } else {
        await new AuthService().forgotPasswordV2Captcha(data);
      }

      history.push('/password-reset-success');
    } catch (exception) {
      if (exception.message?.includes('The response parameter is invalid or malformed.')) {
        history.push('/login');

        enqueueSnackbar('Your reset attemp was rejected', { autoHideDuration: 8000, variant: 'error' });
        return;
      }

      if (exception.statusCode === 429) {
        setErrors('You exceeded the number of attempts allowed. The password reset will be blocked for 10 minutes.');
        enqueueSnackbar('Temporarily blocked', { autoHideDuration: 5000, variant: 'error' });
        return;
      }

      if (exception.message?.includes('Low recaptcha score.')) {
        setUseV3Captcha(false);
        return;
      }

      if (exception instanceof ValidationException) {
        if (exception.errors?.email.some((error) => error.includes('does not exist in database'))) {
          history.push('/password-reset-success');
        }
      } else {
        setRecaptchaToken(null);
        setErrors(exception.message);
      }
    }
  };

  const onVerify = useCallback((token) => {
    setRecaptchaToken(token);
  }, []);

  const onVerifyV2 = useCallback((token) => {
    setRecaptchaV2Token(token);
  }, []);

  const handleSubmit = async () => {
    const validData = await editFieldRef.current.getValidData();

    if (!validData) return;

    if (!!recaptchaToken && useV3Captcha) {
      setRefreshReCaptcha((r) => !r);
      onSubmit({ ...validData, token: recaptchaToken });
    } else if (!useV3Captcha && recaptchaV2Token) {
      onSubmit({ ...validData, token: recaptchaV2Token });
    }
  };

  const goToLogin = () => {
    history.push('/login');
  };

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <Grid container direction="column" justify="flex-end">
        <Grid item className={classes.email}>
          <EditFields
            fields={fields}
            onSave={handleSubmit}
            useLabel
            allowEnterSubmit
            ref={editFieldRef}
            labelPosition="top"
            labelClass={classes.loginLabels}
            validate={User.validateCreate}
          />
        </Grid>

        <Grid item style={{ position: 'relative' }}>
          {useV3Captcha ? (
            <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_KEY}>
              <GoogleReCaptcha onVerify={onVerify} refreshReCaptcha={refreshReCaptcha} />
            </GoogleReCaptchaProvider>
          ) : (
            <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_KEY}>
              <ReCAPTCHA sitekey={process.env.REACT_APP_V2_RECAPTCHA_KEY} onChange={onVerifyV2} />
            </GoogleReCaptchaProvider>
          )}
        </Grid>

        {errors ? (
          <Grid item>
            <Typography align="left" variant="body1" className={classes.errorMessage}>
              {errors}
            </Typography>
          </Grid>
        ) : null}

        <Grid item className={classes.buttonContainer}>
          <Button type="button" variant="contained" color="primary" fullWidth onClick={handleSubmit}>
            Reset Password
          </Button>
        </Grid>

        <Grid item className={classes.buttonContainer}>
          <Button type="button" variant="outlined" color="primary" fullWidth onClick={goToLogin}>
            Cancel
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};
